import { useState } from 'react';
import { Button, Row, Col, Spinner } from 'reactstrap';
import {
  useTranslator,
  useToast,
  useAppContext,
  cloudflareImage,
  mealPlaceholderImage,
  formatCalories,
} from '@front-activerse/common-components';
import { useNavigate, useOutletContext } from 'react-router-dom';

const steps = [5, 15, 30, 45, 60];

const DayPlanMeal = ({
  date,
  preparationTime,
  name,
  mealType,
  kcal,
  loadDayPlanMealReplacements,
  mealId,
  setId,
  preparationImageUrl,
  publicName,
  addMealToFavorites,
  removeMealFromFavorites,
  canBeAddedToFavorites,
  isFavorite,
  originalMealId,
  mealKey,
  mealCustomTag,
}) => {
  const { t } = useTranslator();

  const { toastError, toastSuccess, dismiss } = useToast();
  const {
    consts: { APP_BRAND },
    images: { favouriteFull, favouriteEmpty, buttonChange, addToTrackerIcon },
  } = useAppContext();

  const navigate = useNavigate();

  const [preloadInside, setPreloadInside] = useState(false);
  const { trackFirebaseEvent } = useOutletContext();

  const calculatePreparationTime = (minutes) => {
    if (minutes === 0 || minutes == null) {
      // return '0 min';
      return null;
    }

    for (const step of steps) {
      if (minutes <= step) {
        return `< ${step} min`;
      }
    }
    return `> ${steps[steps.length - 1]} min`;
  };

  const handleMealReplace = () => {
    loadMealReplacements();
    localStorage.setItem('activeTimestamp', 2);
    trackFirebaseEvent('user_action', {
      action: 'tap-change_meal',
      location: 'Dayplan',
      meal_id: mealId,
      meal_category: mealType,
    });
  };

  const handleMealClick = () => {
    localStorage.setItem('activeTimestamp', 1);
    trackFirebaseEvent('user_action', {
      action: 'tap-meal_details',
      location: 'Dietplan',
      meal_id: mealId,
      meal_category: mealType,
    });

    let url;
    if (originalMealId) {
      url = `/meals/day-plan/${date}/${mealId}/${mealType}/${setId}/${originalMealId}`;
    } else {
      // add -1 to originalMealId for properly match function from react router. Used for callNativeApp correct title
      url = `/meals/day-plan/${date}/${mealId}/${mealType}/${setId}/-1`;
    }
    navigate(url);
  };

  const handleAddRemoveMealToFavorites = async () => {
    if (!isFavorite) {
      try {
        const response = await addMealToFavorites.addMeal(mealId);
        if (response.data.me.addMealToFavorites.__typename === 'BasicMutationSuccessWithId') {
          toastSuccess(
            <p className="mb-0">{t('mealPlan.favorites.notification.addedToFav.content')}</p>
          );
          trackFirebaseEvent('user_action', {
            action: 'tap-add_favourites',
            location: 'V_Dayplan',
            meal_id: mealId,
            meal_category: name,
          });
        }
      } catch (error) {
        toastError();
        throw new Error(`Failed to add meal to favorites: ${error}`);
      }
    } else {
      try {
        const response = await removeMealFromFavorites.removeMeal(mealId);
        if (
          response.data.me.removeMealFromFavoritesByMealId.__typename === 'BasicMutationSuccess'
        ) {
          toastSuccess(
            <p className="mb-0">
              {t('mealPlan.favorites.notification.removedFromFav.content')}
              <Button
                color="link"
                onClick={() => {
                  addMealToFavorites.addMeal(mealId);
                  dismiss();
                }}
              >
                {t('meal.favorites.notification.removedFromFav.button')}
              </Button>
            </p>
          );
        }
      } catch (error) {
        toastError();

        throw new Error(`Failed to remove meal from favorites: ${error}`);
      }
    }
  };

  const loadMealReplacements = async () => {
    setPreloadInside(true);
    const request = {
      userDietSetId: setId,
      mealType: mealType,
      dietMealId: mealId,
      limit: 10,
      prependWithOriginalMeal: !!originalMealId,
    };
    const result = await loadDayPlanMealReplacements.load(request);

    if (result.data.me.dailyDietMeal) {
      const replacements = result.data.me.dailyDietMeal.replacements;

      let url;
      if (originalMealId) {
        url = `/meals/day-plan/${date}/${mealId}/${mealType}/${setId}/${originalMealId}/replace`;
      } else {
        url = `/meals/day-plan/${date}/${mealId}/${mealType}/${setId}/-1/replace`;
      }

      localStorage.setItem('activeTimestamp', 2);

      navigate(url, {
        state: {
          replacements,
          from: 'day-plan',
        },
      });
    }

    setPreloadInside(false);
  };

  const renderMealImage = () => {
    let mealImg;
    if (preparationImageUrl) {
      mealImg = preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(mealType, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'mealPlanMealDetails');

    return (
      <>
        <img src={mealImg} className="img-fluid" alt={name} onClick={() => handleMealClick()} />
        {/* hack to preload meal details image */}
        <img src={coverImage} className="d-none" alt="" />
      </>
    );
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/day-plan/module/tracker/add-meal/${mealId}/${mealType}/${setId}/${date}`);
  };

  const calculatedPreparationTime = calculatePreparationTime(preparationTime.minutes);

  return (
    <section className="meal">
      <h2 className="title-medium semi-bold mb-m">{name}</h2>
      <div className="meal-box">
        <Row className="align-items-center">
          <Col md={6}>
            <div className="meal-image">
              {renderMealImage()}

              {canBeAddedToFavorites && (
                <button
                  className="add-remove-fav"
                  type="button"
                  onClick={() => handleAddRemoveMealToFavorites()}
                >
                  <img
                    src={isFavorite ? favouriteFull : favouriteEmpty}
                    alt=""
                    width="32"
                    height="32"
                  />
                </button>
              )}
            </div>
          </Col>
          <Col md={6}>
            {mealCustomTag && mealCustomTag.label && (
              <div className="tags mb-2">
                <span
                  className="tag me-1"
                  style={{ backgroundColor: mealCustomTag.color, color: '#fff' }}
                >
                  {mealCustomTag.label}
                </span>
              </div>
            )}

            <div role="button" className="meal-title" onClick={() => handleMealClick()}>
              <h4 className="title-medium semi-bold mb-0">{publicName}</h4>
            </div>

            <div className="meal-meta">
              <p className="mb-0">
                <span>{formatCalories(kcal)}&nbsp;kcal</span>
                {calculatedPreparationTime && (
                  <>
                    <strong className="bullet-separator mx-1">•</strong>
                    <span>{calculatedPreparationTime}</span>
                  </>
                )}
              </p>
            </div>
          </Col>
        </Row>

        <hr />

        <Row className="gx-0">
          <Col xs={4} md={6}>
            <Button color="link" size="sm" onClick={() => handleMealReplace()} className="w-100">
              {preloadInside ? (
                <Spinner color="primary" size="sm" className="me-2">
                  {t('loading.message')}
                </Spinner>
              ) : (
                <img src={buttonChange} className="me-2" alt={t('meal.changeMealButton')} />
              )}
              {t('meal.changeMealButton')}
            </Button>
          </Col>
          <Col xs={8} md={6}>
            <Button
              color="primary"
              size="sm"
              className="w-100 px-3 tracker"
              onClick={handleAddMealToTracker}
            >
              <img
                src={addToTrackerIcon}
                alt={t('tracker.meal.addToTrackerButton')}
                className="me-2"
              />
              {t('tracker.meal.addToTrackerButton')}
            </Button>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default DayPlanMeal;
